// Words and numbers for the stock screens (DESIGN.md §9: sentence case, plain words).
import { formatMoney, moneyFromCents, splitIntoPacks } from '@wodi/domain';
import type { TagState } from '@wodi/ui';

const grouping = new Intl.NumberFormat('en-KE', { maximumFractionDigits: 0 });

/** `1,250 tablet` — the base unit always follows the number. */
export function formatQuantity(quantity: number, unit: string): string {
  return `${grouping.format(quantity)} ${unit}`;
}

/** `3 × Box of 100 and 20 tablet`, for a quantity that fills packs. */
export function formatInPacks(
  quantity: number,
  unit: string,
  pack: { display: string; baseUnitsPerPack: number } | undefined,
): string {
  if (pack === undefined || pack.baseUnitsPerPack <= 1) return formatQuantity(quantity, unit);
  const { packs, loose } = splitIntoPacks(quantity, pack.baseUnitsPerPack);
  if (packs === 0) return formatQuantity(loose, unit);
  const whole = `${grouping.format(packs)} × ${pack.display}`;
  return loose === 0 ? whole : `${whole} and ${formatQuantity(loose, unit)}`;
}

/** Cents as `KES 1,200.50`. */
export function formatCents(cents: string): string {
  return formatMoney(moneyFromCents(BigInt(cents)));
}

export const MOVEMENT_LABELS = {
  opening_balance: 'Opening balance',
  receipt: 'Received',
  receipt_reversal: 'Receipt reversed',
  transfer_out: 'Transferred out',
  transfer_in: 'Transferred in',
  adjustment: 'Adjusted',
  count_variance: 'Count difference',
  dispense: 'Dispensed',
  dispense_return: 'Returned from dispensing',
  sale: 'Sold at the counter',
  sale_return: 'Returned from a sale',
  issue: 'Issued for maintenance',
} as const;

export const REASON_LABELS = {
  damaged: 'Damaged',
  expired: 'Expired',
  lost: 'Lost',
  theft_suspected: 'Theft suspected',
  found: 'Found',
  data_correction: 'Data correction',
  other: 'Other',
  opening_balance: 'Opening balance',
} as const;

export const STATUS_LABELS = {
  requested: 'Waiting for approval',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  in_progress: 'Counting',
  submitted: 'Waiting for approval',
  draft: 'Draft',
  posted: 'Posted',
} as const;

/** Tag shape for a stock document status: waiting is info, refused is failed, closed is neutral. */
export const STATUS_TAGS: Record<keyof typeof STATUS_LABELS, TagState> = {
  requested: 'pending',
  approved: 'done',
  rejected: 'failed',
  cancelled: 'neutral',
  in_progress: 'active',
  submitted: 'pending',
  draft: 'pending',
  posted: 'done',
};

export const REORDER_LABELS = {
  out_of_stock: 'Out of stock',
  at_or_below_reorder_level: 'Reorder',
  ok: 'In stock',
  not_set: 'In stock',
} as const;

/** Days to expiry in words: `Expired 3 days ago`, `Expires today`, `Expires in 45 days`. */
export function expiryWords(daysToExpiry: number | null): string {
  if (daysToExpiry === null) return 'No expiry';
  if (daysToExpiry < 0)
    return `Expired ${-daysToExpiry} ${-daysToExpiry === 1 ? 'day' : 'days'} ago`;
  if (daysToExpiry === 0) return 'Expires today';
  return `Expires in ${daysToExpiry} ${daysToExpiry === 1 ? 'day' : 'days'}`;
}

/** Whole, non-negative quantities typed as digits (grouping allowed); anything else is null. */
export function readQuantity(text: string): number | null {
  const cleaned = text.replace(/[,\s]/g, '');
  if (!/^\d{1,10}$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return value <= 2_147_483_647 ? value : null;
}
